import { Description } from 'components/description';
import { Layout } from 'components/layout';
import { CustomMetadata } from 'components/metadata';
import { CardsMedium } from 'components/slider/cards/medium';
import { Anchor } from 'constants/anchors';
import { FAQComponent } from '../../casino-faq';
import { CasinoHeader } from './casino-header';
import { CasinoInspections } from './casino-inspections';
import { NavigationBar } from './casino-navigations';
import { TabelDescription } from './casino-table';

import { allMetadata } from 'constants/metadata';
import { useTrustedCasinos } from 'hooks';
import { shuffle } from 'lodash';

import { Icon } from 'common/icon';
import { Breadcrumbs } from 'components/bredcrambs';
import { Analytics } from 'constants/analytics';
import { Routes } from 'constants/routes';
import { casinoPage as casinoPageBreadcrumbs } from 'constants/routes-breadcrumbs';
import { ICasino, INavItem, ISlotReview, IComplaint } from 'constants/types';
import { useMemo } from 'react';
import { getItemIdentifier } from 'utils/slug';
import { ComplaintSection } from './casino-complaints';
import styles from './styles.module.css';
import { CasinoNotification } from './notification';
import { ReviewTableCommentsCasino } from './casino-reviews-table';
import { Reviews } from 'components/table-reviews';
import { CasinosLine } from '../home';

export type Props = {
  casino: ICasino;
  rtpReviews: ISlotReview[];
  complaints?: IComplaint[];
};

export const CasinoPage = ({ casino, rtpReviews, complaints = [] }: Props) => {
  const { trustedCasinos, trustedCasinosCount } = useTrustedCasinos();

  const complaintsArray = useMemo(() => complaints ?? [], [complaints]);
  const trustedCasinosCollection = useMemo(
    () => ({
      _id: 'all-casinos',
      slots_ids: [] as string[],
      casinos_ids: [] as string[],
      customUrl: Routes.allCasinos,
      casinos: trustedCasinos,
      name: 'Casinos with high RTP',
      count: trustedCasinos?.length.toString(),
    }),
    [trustedCasinos]
  );

  const navItems: INavItem[] = [
    {
      icon: <Icon.Search width="16" height="16" />,
      text: 'RTP checks',
      anchor: `#${Anchor.RtpReviews}`,
    },

    {
      icon: <Icon.MessageFifth />,
      text: 'Player reviews',
      anchor: `#${Anchor.CasinoReviews}`,
    },

    {
      icon: <Icon.UserSecond fill="white" />,
      text: 'Complaints',
      anchor: `#${Anchor.CasinoComplaints}`,
    },

    {
      icon: <Icon.AlignLeft />,
      text: 'Info',
      anchor: `#${Anchor.CasinoDetails}`,
    },

    {
      icon: <Icon.Question width={20} height={20} />,
      text: 'FAQ',
      anchor: `#${Anchor.FAQ}`,
    },
  ];

  return (
    <div className="wrapper">
      <CustomMetadata metadata={allMetadata.casinoPage(casino)} />
      <Layout hasFooter isSearchInputMobileVisible>
        <main className={styles.main}>
          <div className={styles.container}>
            <Breadcrumbs
              enableAbsoluteOnMobile={false}
              items={[
                ...casinoPageBreadcrumbs,
                {
                  id: 2,
                  path: Routes.casinoPage(getItemIdentifier(casino)),
                  title: casino?.name ?? '',
                },
              ]}
            />
            <CasinoHeader data={casino} />
            {casino?.isClosed && (
              <CasinosLine collection={trustedCasinosCollection} />
            )}
            <CasinoNotification data={casino} />
            <div className={styles.description}>
              <Description data={casino} />
            </div>
            <div className={styles.navigation}>
              <NavigationBar
                arrow
                items={navItems}
                variantBg="notransparent"
                variantPadding="medium"
              />
            </div>
            <CasinoInspections data={casino} />
            <Reviews
              limit={6}
              casino={casino}
              results={rtpReviews}
              count={casino?.slots_reviews_count}
              name={`${casino?.name} Slots RTP reviews`}
              isProviderVisible
            />
            <ReviewTableCommentsCasino casino={casino} />
            <ComplaintSection
              casino={casino}
              complaints={complaintsArray}
              count={complaintsArray.length}
            />
            <TabelDescription data={casino} />
            <FAQComponent
              customFaq={casino.faq}
              titleClassName={styles.faq_title}
            />
            <CardsMedium
              headerId={Analytics.casino.similarCasinos}
              name="Casinos with high RTP"
              count={trustedCasinosCount}
              data={shuffle(
                trustedCasinos.filter((item) => item?._id !== casino?._id)
              )?.slice(0, 4)}
              url={Routes.casinoCollections('trusted-casinos')}
            />
          </div>
        </main>
      </Layout>
    </div>
  );
};
